/**
 * @fileoverview Feature Flags Centralizadas
 *
 * Este arquivo centraliza TODAS as feature flags do sistema.
 * Baseado em boas práticas de Martin Fowler (Feature Toggles) e 12-Factor App.
 *
 * @see https://martinfowler.com/articles/feature-toggles.html
 * @see https://12factor.net/config
 *
 * TIPOS DE FLAGS:
 * - Release Toggles: Features incompletas (curta duração)
 * - Ops Toggles: Controle operacional (kill switches)
 * - Experiment Toggles: A/B testing
 * - Permission Toggles: Features por perfil de usuário
 *
 * PRINCÍPIOS:
 * 1. Configuração separada do código
 * 2. Flags granulares e ortogonais
 * 3. Valores default seguros para produção
 * 4. Fácil de remover quando não necessário
 *
 * @example
 * import { FEATURE_FLAGS, shouldUseMock } from '@/config/featureFlags';
 *
 * if (shouldUseMock('schedules')) {
 *   // usa mock - backend não existe
 * }
 */

import { logger } from "@/utils/logger";

// ============================================================
// DETECÇÃO DE AMBIENTE
// ============================================================

const isDevelopment = process.env.NODE_ENV === "development";
const isProduction = process.env.NODE_ENV === "production";
const isTest = process.env.NODE_ENV === "test";

// ============================================================
// FEATURE FLAGS
// ============================================================

/**
 * Configuração centralizada de feature flags
 *
 * IMPORTANTE:
 * - Em produção, mocks devem estar TODOS desabilitados
 * - Apenas features que REALMENTE não têm backend usam mock
 */
export const FEATURE_FLAGS = Object.freeze({
  // ========================================
  // MOCKS - Features SEM backend implementado
  // ========================================
  mock: Object.freeze({
    /**
     * Escalas médicas - backend NÃO implementado
     * @type {boolean}
     */
    schedules: true,

    /**
     * Assistente IA para escalas - backend NÃO implementado
     * @type {boolean}
     */
    scheduleAssistant: true,

    /**
     * Agenda semanal do médico (modal "Ver agenda" em Gestão de Médicos) -
     * endpoint real existe (PRD-062, E-3; contrato em
     * src/services/doctorAgendaService.js). Ligar só para desenvolver sem backend.
     * @type {boolean}
     */
    doctorAgenda: false,

    /**
     * Visão geral de agendamentos (calendário de todos os profissionais,
     * "Operação > Agenda") - backend implementado (PRD-065); contrato em
     * src/services/agendaOverviewService.js. Ligar só para desenvolver sem backend.
     * @type {boolean}
     */
    agendaOverview: false,

    /**
     * Exportação da agenda para Excel ("Operação > Agenda", SPEC-003) -
     * backend implementado (PRD-066, ptm-core-api#2490); contrato em
     * src/services/agendaOverviewService.js. Ligar só para desenvolver sem
     * backend (baixa um CSV de demonstração).
     * @type {boolean}
     */
    agendaExport: false,

    /**
     * Dashboards da Mensageria (SPEC-004) - backend implementado (hmx-sms-sp#149,
     * via BFF do HC-SP). Fora de adminsaude.sosportal.com.br o serviço já usa o
     * mock (src/services/pipelineHcspBase.js isLiveHost). Ligar só para forçar
     * o mock (números de 09/10/2026) também em produção.
     * @type {boolean}
     */
    messagingDashboards: false,

    /**
     * Controles de disparo (HC-SP) - backend implementado (pipeline /dispatch, via BFF do HC-SP).
     * Fora de adminsaude.sosportal.com.br o serviço já usa o mock. Ligar só para forçar o mock
     * também em produção (nada é ligado nem desligado de verdade).
     * @type {boolean}
     */
    dispatchControls: false,

    /**
     * Alertas operacionais (HC-SP) - backend implementado (pipeline /alerts, via BFF do HC-SP).
     * Fora de adminsaude.sosportal.com.br o serviço já usa o mock. Ligar só para forçar o mock
     * também em produção (nenhum alerta é ligado nem desligado de verdade).
     * @type {boolean}
     */
    operationalAlerts: false,

    /**
     * Campanhas (HC-SP) - backend implementado (pipeline GET /campaigns, via BFF do HC-SP). Fora de
     * adminsaude.sosportal.com.br o serviço já usa o mock. Ligar só para forçar o mock também em produção.
     * @type {boolean}
     */
    campaigns: false,
  }),

  // ========================================
  // BACKENDS REAIS DISPONÍVEIS
  // ========================================
  // Estas entidades TÊM backend e devem SEMPRE usar real:
  // - healthCenters: /core/v1/HealthCenters
  // - specialties: /core/v1/Specialty
  // - doctors: /core/v1/Doctors/*
  // - patients: /core/v1/Patients/*
  // - admins: /core/v1/Admins/*
  // - queue: /v1/Requisitions/*

  // ========================================
  // CONFIGURAÇÕES DE DESENVOLVIMENTO
  // ========================================
  dev: Object.freeze({
    /**
     * Habilita logs detalhados de API
     * @type {boolean}
     */
    verboseApiLogs: isDevelopment,

    /**
     * Delay artificial em requisições mock (ms)
     * Útil para testar estados de loading
     * @type {number}
     */
    mockDelay: isDevelopment ? 300 : 0,

    /**
     * Habilita devtools de debug
     * @type {boolean}
     */
    enableDevtools: isDevelopment || isTest,
  }),

  // ========================================
  // CACHE
  // ========================================
  cache: Object.freeze({
    /**
     * TTL do cache de dados mestres (ms)
     * Health Centers, etc.
     * @type {number}
     */
    masterDataTTL: 15 * 60 * 1000, // 15 minutos

    /**
     * TTL do cache de especialidades (ms)
     * @type {number}
     */
    specialtiesTTL: 30 * 60 * 1000, // 30 minutos

    /**
     * Habilita cache de dados mestres
     * @type {boolean}
     */
    enabled: true,
  }),

  // ========================================
  // OPS TOGGLES - Controle operacional
  // ========================================
  ops: Object.freeze({
    /**
     * Habilita funcionalidade de fila em tempo real
     * Pode ser desabilitado em caso de sobrecarga
     * @type {boolean}
     */
    liveQueueEnabled: true,

    /**
     * Intervalo de polling da fila (ms)
     * @type {number}
     */
    queuePollingInterval: 30000, // 30 segundos

    /**
     * Habilita notificações push
     * @type {boolean}
     */
    pushNotificationsEnabled: true,

    /**
     * Campo "Fluxo de atendimento" em Editar médico (HC-SAMPA, PRD-057 RF-8).
     * Escrita: PRD-057 fase 4 §2.4 (Bearer + Admin/HCAdmin).
     * Leitura: `attendance_flow` e `company` no grid do admin, resolvidos em
     * memória por CNS (PRD-057 RF-13, commit 0447bb056 em origin/dev).
     *
     * ⚠️ LIGADA SÓ EM DEV, de propósito. A branch de promoção
     * (release/PRD-062-agenda-e-vinculo-master), master e staging levam o PATCH
     * mas NÃO levam a leitura — 0447bb056 não foi cherry-pickado. Ligar em
     * produção antes disso deixaria a tela com escrita funcionando e leitura
     * sempre nula. Trocar por `true` quando a leitura chegar ao ambiente alvo.
     * @type {boolean}
     */
    doctorAttendanceFlowEnabled: isDevelopment,
  }),

  // ========================================
  // PWA — App instalável (PRD-022 INC-1) + push (INC-2)
  // ========================================
  pwa: Object.freeze({
    /**
     * App instalável: registra o service worker (cache de estáticos) e habilita
     * o convite de instalação (Android `beforeinstallprompt` / guia iOS).
     * Kill switch de rollout. O registro do SW só ocorre em contexto seguro
     * (HTTPS ou localhost) — ver registerServiceWorker.js.
     * @type {boolean}
     */
    installEnabled: true,

    /**
     * Push (FCM-for-Web, PRD-022 INC-2): mostra o card de notificações em
     * Configurações (opt-in + push de teste do GodAdmin). Degrada graciosamente
     * se o Firebase não estiver configurado (VUE_APP_FIREBASE_*). Kill switch.
     * @type {boolean}
     */
    pushEnabled: true,
  }),
});

// ============================================================
// HELPERS
// ============================================================

/**
 * Verifica se uma entidade deve usar mock
 *
 * @param {keyof typeof FEATURE_FLAGS.mock} entity - Nome da entidade
 * @returns {boolean} true se deve usar mock
 *
 * @example
 * if (shouldUseMock('schedules')) {
 *   return mockBackend.schedules.list();
 * }
 */
export function shouldUseMock(entity) {
  // Em testes, permite override via variável de ambiente
  if (isTest && process.env.FORCE_REAL_API === "true") {
    return false;
  }

  return FEATURE_FLAGS.mock[entity] ?? false;
}

/**
 * Verifica se uma feature está habilitada
 *
 * @param {string} category - Categoria da flag (mock, dev, cache, ops)
 * @param {string} feature - Nome da feature
 * @returns {boolean}
 */
export function isFeatureEnabled(category, feature) {
  return FEATURE_FLAGS[category]?.[feature] ?? false;
}

/**
 * Retorna valor de configuração
 *
 * @param {string} category - Categoria
 * @param {string} key - Chave
 * @param {*} defaultValue - Valor padrão
 * @returns {*}
 */
export function getConfig(category, key, defaultValue = null) {
  return FEATURE_FLAGS[category]?.[key] ?? defaultValue;
}

/**
 * Log condicional para desenvolvimento
 *
 * @param {...any} args - Argumentos para log
 */
export function devLog(...args) {
  if (FEATURE_FLAGS.dev.verboseApiLogs) {
    logger.log("[DEV]", ...args);
  }
}

/**
 * Log de warning para desenvolvimento
 *
 * @param {...any} args - Argumentos para log
 */
export function devWarn(...args) {
  if (FEATURE_FLAGS.dev.verboseApiLogs) {
    logger.warn("[DEV:WARN]", ...args);
  }
}

// ============================================================
// VALIDAÇÃO EM PRODUÇÃO
// ============================================================

// Alerta se mocks estiverem habilitados em produção
if (isProduction) {
  const enabledMocks = Object.entries(FEATURE_FLAGS.mock)
    .filter(([, enabled]) => enabled)
    .map(([name]) => name);

  if (enabledMocks.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      "[FEATURE_FLAGS] ⚠️ Mocks habilitados em PRODUÇÃO:",
      enabledMocks.join(", "),
      "\nIsso é esperado apenas para features sem backend.",
    );
  }
}

// ============================================================
// EXPORTS DEFAULT
// ============================================================

export default FEATURE_FLAGS;
